import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  updateProfile,
  sendPasswordResetEmail,
} from 'firebase/auth';
import type { UserCredential } from 'firebase/auth';
import { doc, getDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { downloadAndStoreProfilePhoto } from './storageService';
import { createUserProfile } from './userService';
import { updateLastLogin } from './userService';

// Google OAuth Provider
const googleProvider = new GoogleAuthProvider();

// Sign up with email and password
export async function signUpWithEmail(
  email: string,
  password: string,
  displayName: string
): Promise<UserCredential> {
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);

  // Update profile with display name
  await updateProfile(userCredential.user, { displayName });

  // Create user profile in Firestore
  // Handle profile creation errors gracefully - auth still succeeds
  try {
    await createUserProfile(userCredential.user, false);
  } catch (error) {
    console.error('Failed to create user profile, but authentication succeeded:', error);
    // Don't throw - authentication was successful
  }

  return userCredential;
}

// Sign in with email and password
export async function signInWithEmail(
  email: string,
  password: string
): Promise<UserCredential> {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  await updateLastLogin(userCredential.user.uid);
  return userCredential;
}

// Sign in with Google
export async function signInWithGoogle(): Promise<UserCredential> {
  const userCredential = await signInWithPopup(auth, googleProvider);

  // Check if user profile exists, create if not
  // Handle profile creation errors gracefully - auth still succeeds
  try {
    const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
    if (!userDoc.exists()) {
      await createUserProfile(userCredential.user, true);
    } else {
      await updateLastLogin(userCredential.user.uid);
      // Profile exists - check if we need to download OAuth photo
      const profile = userDoc.data();
      if (userCredential.user.photoURL && !profile.photoURL) {
        // User has OAuth photo but no stored photo - download it
        console.log('Downloading OAuth profile photo...');
        try {
          const storedPhotoURL = await downloadAndStoreProfilePhoto(
            userCredential.user.uid,
            userCredential.user.photoURL
          );
          await updateDoc(doc(db, 'users', userCredential.user.uid), {
            photoURL: storedPhotoURL,
            updatedAt: serverTimestamp(),
          });
        } catch (error) {
          console.error('Failed to download OAuth photo:', error);
        }
      }
    }
  } catch (error) {
    console.error('Failed to create user profile, but authentication succeeded:', error);
    // Don't throw - authentication was successful
  }

  return userCredential;
}

// Sign out
export async function logout(): Promise<void> {
  return signOut(auth);
}

// Send password reset email
export async function sendPasswordReset(email: string): Promise<void> {
  return sendPasswordResetEmail(auth, email);
}
